import { useCallback, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "styled-components";
import "./App.scss";
import { BotTheme, theme as default_theme } from "./Customization/Theme";
import { ChatbotConsoleService } from "./Services/Apis";
import { HostWindowCommService } from "./Services/IframeHostService/HostWindowCommService";
import { IEvents, OEvents } from "./Services/IframeHostService/models";
import { useAppDispatch, useAppSelector } from "./Store";
import {
  StoreBotConfigFn
} from "./Store/Slices/ChatbotSlices/BotConfigSlice";
import {
  setIsSessionIdFromLocalStorage,
  setSessionId,
} from "./Store/Slices/socket/AllMessagesSlice";
import {
  botInfoStore,
  setBotId,
  setBotToFullScreen,
  setBotToMobileView,
  setChatbotParams,
  setGetaHost,
  setIdentityId,
  setSiteId,
  setLocation,
  setOpenStatus,
  setPipPendingConfirm,
  setIsPipInstance,
  setVisitorId,
  setWidgetPreviewType,
} from "./Store/Slices/socket/BotInfoSlice";
import { setDeviceSize } from "./Store/Slices/socket/DeviceSizeSlice";
import Home from "./socket/screens/Home";
import { PreviewType, SOCKET_CONNECTION_TYPES } from "./socket/utils/Enum";
import GlobalStyles from "./globalStyles";
import { ensureIndustryFontLoaded } from "./Utils/IndustryFontLoader";

const WidgetApp = () => {
  const [theme, setTheme] = useState<any>(default_theme);
  const [isThemeLoadedFromConfig, setIsThemeLoadedFromConfig] =
    useState<boolean>(false);

  const dispatch = useAppDispatch();
  // const { botConfig } = useAppSelector(useBotConfigStore);

  const { botId, toggleReloadWidget, getaHost, location } =
    useAppSelector(botInfoStore);

  let myBotTheme = null;
  let commService = useMemo(() => {
    return HostWindowCommService.getInstance();
  }, []);

  const onChatbotStart = useCallback((event: any) => {
    if (event.data && event.data.hasOwnProperty("data")) {
      const data = event.data.data;
      // The parent-page loader script owns this payload's shape; a malformed
      // send (data present as a key but undefined/null) must not crash the
      // widget outright — hasOwnProperty above only confirms the key exists,
      // not that its value is a usable object.
      if (!data) return;
      if (data.hasOwnProperty("bot_id")) {
        dispatch(setBotId(data.bot_id));
      }
      if (data.hasOwnProperty("location")) {
        dispatch(setLocation(data.location));
      }
      if (data?.["is_widget_preview"]) {
        dispatch(setWidgetPreviewType(data?.["is_widget_preview"]));

        if (data?.["is_widget_preview"] === PreviewType.getaWidgetPreview) {
          // web bot preview for geta
          SOCKET_CONNECTION_TYPES.BOT_CONVERSATION = "bot-conversation-preview";
          SOCKET_CONNECTION_TYPES.USER_CONNECT = "";
        }
      }
      if (data.hasOwnProperty("device")) {
        dispatch(setDeviceSize(data.device));
      }

      if (data.hasOwnProperty("geta_host") && data.geta_host) {
        dispatch(setGetaHost(data.geta_host));
      }
      if (
        data.hasOwnProperty("chatbot_session_id") &&
        data.chatbot_session_id
      ) {
        dispatch(setSessionId(data?.chatbot_session_id));
        dispatch(setIsSessionIdFromLocalStorage(true));
      }
      if (data?.isFullScreenBot) {
        dispatch(setBotToFullScreen(true));
      }
      if (data?.isMobileView) {
        dispatch(setBotToMobileView(true));
      }
      if (data?.chatbot_params) {
        dispatch(setChatbotParams(data?.chatbot_params));
      }
      // The PiP copy of the widget (opened by the loader's openPipWindow())
      // boots into an existing, already-open conversation.
      if (data?.is_pip) {
        // Stays true for the rest of this iframe's life, unlike
        // pipPendingConfirm (which flips back to false once confirmed) —
        // used by Header.tsx to hide the expand/minimize/end-chat buttons,
        // since the PiP window already has Chrome's own native controls.
        dispatch(setIsPipInstance(true));
        if (data?.pip_pending_confirm) {
          // Don't show the normal closed/bubble state, but also don't jump
          // straight into the chat panel — ask the user to confirm first
          // (Home.tsx gates on this flag and renders PipConfirmScreen
          // instead of the normal chat panel until the user accepts). The
          // confirm screen's own natural read-and-decide delay also happens
          // to give the backend time to finish persisting a just-sent
          // message before history is fetched — fetching immediately
          // instead can race that and show stale history.
          dispatch(setPipPendingConfirm(true));
        } else {
          // The loader already knows the user confirmed once this page
          // load (its own in-memory flag, reset on refresh) and opened this
          // window at the full chat panel size directly — skip the confirm
          // screen and open straight into the conversation.
          dispatch(setOpenStatus(true));
        }
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChatbotSaveVisitorId = (event: any) => {
    if (event.data && event.data.hasOwnProperty("data")) {
      const data = event.data.data;
      if (!data) return;
      if (data.hasOwnProperty("visitor_id")) {
        dispatch(setVisitorId(data.visitor_id));
      }
      if (data.hasOwnProperty("identity_id")) {
        dispatch(setIdentityId(data.identity_id));
      }
      if (data.hasOwnProperty("site_id")) {
        dispatch(setSiteId(data.site_id));
      }
    }
  }; // eslint-disable-line react-hooks/exhaustive-deps

  const getWidgetFrame = () => {
    commService.registerEventListener(IEvents.CHATBOT_START, onChatbotStart);
    commService.registerEventListener(
      IEvents.CHATBOT_SAVE_VISITOR_ID,
      onChatbotSaveVisitorId
    );
  }; // eslint-disable-line react-hooks/exhaustive-deps

  const getBotWidgetConfigFn = (id: string) => {
    setIsThemeLoadedFromConfig(false);
    // `location` is the PARENT page URL from CHATBOT_START (loader-injected),
    // not window.location. Its origin → backend resolves the per-origin Matomo
    // siteId. document.referrer is empty inside the bot iframe, so use location.
    let parentOrigin = "";
    try {
      parentOrigin = location ? new URL(location).origin : document?.referrer || "";
    } catch (e) {
      parentOrigin = document?.referrer || "";
    }
    const CONFIG = {
      "GETA-HOST": getaHost,
      "X-Parent-Origin": parentOrigin,
    };
    let parentURL: any = [];
    try {
      if (location) {
        parentURL = new URL(location).searchParams;
      }
    } catch (e) {
      parentURL = [];
    }
    const paramsObject: any = {
      bot_id: id,
    };
    parentURL.forEach((value: any, key: any) => {
      paramsObject[key] = value;
    });
    ChatbotConsoleService.getBotWidgetConfig({ ...paramsObject }, CONFIG)
      .then((res: any) => {
        const data = res?.data;

        const gatedTheme = {
          ...data?.chatbot_theme,
          industry_style: {
            ...(data?.chatbot_theme?.industry_style || {}),
            // Modern Style is always on; the console no longer has a switch
            // to store a false here, and a stale stored false must not
            // stick a bot in the classic look.
            enabled: true,
          },
        };

        dispatch(
          StoreBotConfigFn({
            botConfig: {
              chatbot: data?.chatbot,
              theme: gatedTheme,
              config: data?.chatbot_configuration,
            },
          })
        );
        myBotTheme = new BotTheme(gatedTheme, data?.chatbot_configuration);
        setTheme(myBotTheme?.theme);
        setIsThemeLoadedFromConfig(true);

        ensureIndustryFontLoaded(myBotTheme?.theme?.font?.primary?.family);

        //* Set siteId from config directly so /query has it on load, instead of
        //* waiting for the tracking-script echo (which races the first message).
        const resolvedSiteId = data?.chatbot_configuration?.siteId ?? null;
        if (resolvedSiteId) {
          dispatch(setSiteId(resolvedSiteId));
        }

        //* sending widget configuration to tracking
        commService.postMessage({
          event_type: OEvents.CHATBOT_CONFIGURATION,
          data: data?.chatbot_configuration || null,
        });
      })
      .catch((err: any) => {
        console.error("bot config theme api error", err);
      });
  };

  useEffect(() => {
    getWidgetFrame();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (botId?.length > 0) {
      getBotWidgetConfigFn(botId);
    } else {
      // console.error("bot id not found");
    }
  }, [botId, toggleReloadWidget]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isThemeLoadedFromConfig) {
    return null;
  }

  return (
    // @ts-ignore
    <ThemeProvider theme={theme}>
      {/* @ts-ignore */}
      <GlobalStyles $fontFamily={theme?.font?.primary?.family ?? "Inter"} />
      <div className="App">{botId && <Home />}</div>
    </ThemeProvider>
  );
};

export default WidgetApp;
